import React, { useState, useRef, useEffect, useMemo } from 'react';
import Tippy from '@tippyjs/react/headless';

export interface DatePickerProps {
  /** 当前选中的日期字符串 (YYYY-MM-DD) */
  value?: string;
  /** 默认值 (YYYY-MM-DD) */
  defaultValue?: string;
  /** 日期改变回调 */
  onChange?: (dateString: string) => void;
  /** 占位提示 */
  placeholder?: string;
  /** 最小可选日期 (YYYY-MM-DD) */
  minDate?: string;
  /** 最大可选日期 (YYYY-MM-DD) */
  maxDate?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否允许清空 */
  clearable?: boolean;
  /** 尺寸风格 */
  size?: 'sm' | 'md' | 'lg';
  /** 最外层容器样式类 */
  className?: string;
  /** 输入框样式类 */
  inputClassName?: string;
  /** 日历面板样式类 */
  popoverClassName?: string;
  /** 错误信息 */
  error?: string;
  /** 表单 name 属性 */
  name?: string;
  /** 弹出位置方向，不传则由 Tippy 自动控制 */
  placement?: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end';
}

/** 格式化 Date 为 YYYY-MM-DD */
const formatDate = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** 解析 YYYY-MM-DD 为 Date 对象 */
const parseDate = (str?: string): Date | null => {
  if (!str) return null;
  const parts = str.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  defaultValue,
  onChange,
  placeholder = '请选择日期',
  minDate,
  maxDate,
  disabled = false,
  clearable = true,
  size = 'md',
  className = '',
  inputClassName = '',
  popoverClassName = '',
  error,
  name,
  placement,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string>(value !== undefined ? value : defaultValue || '');

  // 内部维护日历视图的当前年月
  const [viewDate, setViewDate] = useState<Date>(() => {
    return parseDate(value || defaultValue) || new Date();
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // 同步外部 value 变化
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
      const parsed = parseDate(value);
      if (parsed) {
        setViewDate(parsed);
      }
    }
  }, [value]);

  // Click Outside 和 Esc 监听
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsidePopover = popoverRef.current?.contains(target);
      if (!isInsideContainer && !isInsidePopover) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth(); // 0-11

  // 切换月份
  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewDate(new Date(viewYear, viewMonth - 1, 1));
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewDate(new Date(viewYear, viewMonth + 1, 1));
  };

  const handlePrevYear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewDate(new Date(viewYear - 1, viewMonth, 1));
  };

  const handleNextYear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewDate(new Date(viewYear + 1, viewMonth, 1));
  };

  // 快捷选项点击
  const handleQuickSelect = (type: 'today' | 'week' | 'month', e: React.MouseEvent) => {
    e.stopPropagation();
    const target = new Date();
    if (type === 'week') {
      target.setDate(target.getDate() + 7);
    } else if (type === 'month') {
      target.setMonth(target.getMonth() + 1);
    }
    const formatted = formatDate(target);
    selectDate(formatted);
  };

  const selectDate = (dateStr: string) => {
    if (disabled) return;
    setInternalValue(dateStr);
    onChange?.(dateStr);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setInternalValue('');
    onChange?.('');
  };

  // 生成网格天数数据
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
    const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 是周日
    const totalDaysInMonth = lastDayOfMonth.getDate();

    const days: Array<{
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isDisabled: boolean;
      isToday: boolean;
      isSelected: boolean;
    }> = [];

    const todayStr = formatDate(new Date());

    // 补充上个月的尾巴
    const prevMonthLastDay = new Date(viewYear, viewMonth, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(viewYear, viewMonth - 1, prevMonthLastDay - i);
      const dateStr = formatDate(d);
      days.push({
        dateStr,
        dayNumber: d.getDate(),
        isCurrentMonth: false,
        isDisabled: checkIsDisabled(dateStr, minDate, maxDate),
        isToday: dateStr === todayStr,
        isSelected: dateStr === internalValue,
      });
    }

    // 当月天数
    for (let day = 1; day <= totalDaysInMonth; day++) {
      const d = new Date(viewYear, viewMonth, day);
      const dateStr = formatDate(d);
      days.push({
        dateStr,
        dayNumber: day,
        isCurrentMonth: true,
        isDisabled: checkIsDisabled(dateStr, minDate, maxDate),
        isToday: dateStr === todayStr,
        isSelected: dateStr === internalValue,
      });
    }

    // 补充下个月的开头，补满 42 格 (6周)
    const remainingCount = 42 - days.length;
    for (let i = 1; i <= remainingCount; i++) {
      const d = new Date(viewYear, viewMonth + 1, i);
      const dateStr = formatDate(d);
      days.push({
        dateStr,
        dayNumber: i,
        isCurrentMonth: false,
        isDisabled: checkIsDisabled(dateStr, minDate, maxDate),
        isToday: dateStr === todayStr,
        isSelected: dateStr === internalValue,
      });
    }

    return days;
  }, [viewYear, viewMonth, minDate, maxDate, internalValue]);

  // 检查是否受 minDate / maxDate 限制
  function checkIsDisabled(dateStr: string, min?: string, max?: string) {
    if (min && dateStr < min) return true;
    if (max && dateStr > max) return true;
    return false;
  }

  // 尺寸映射表
  const sizeStyles = {
    sm: 'h-9 px-3 text-xs rounded-md',
    md: 'h-[46px] px-4 text-sm rounded-lg',
    lg: 'h-12 px-5 text-base rounded-xl',
  };

  const weekHeaders = ['日', '一', '二', '三', '四', '五', '六'];

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {name && <input type="hidden" name={name} value={internalValue} />}

      <Tippy
        interactive={true}
        visible={isOpen}
        onClickOutside={() => setIsOpen(false)}
        placement={placement}
        offset={[0, 6]}
        appendTo={() => document.body}
        render={(attrs) => (
          <div
            {...attrs}
            ref={popoverRef}
            className={`z-[100] w-72 rounded-2xl border border-background-200 bg-white p-4 shadow-xl shadow-black/10 animate-in fade-in slide-in-from-top-2 duration-150 ${popoverClassName}`}
          >
            {/* 日历头部：切换年月 */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-background-100">
              <div className="flex items-center gap-1 text-foreground-500">
                <button
                  type="button"
                  onClick={handlePrevYear}
                  title="上一年"
                  className="p-1 rounded-md hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-double-left-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  title="上一月"
                  className="p-1 rounded-md hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-base"></i>
                </button>
              </div>

              <div className="text-sm font-semibold text-foreground-900">
                {viewYear}年 {viewMonth + 1}月
              </div>

              <div className="flex items-center gap-1 text-foreground-500">
                <button
                  type="button"
                  onClick={handleNextMonth}
                  title="下一月"
                  className="p-1 rounded-md hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-base"></i>
                </button>
                <button
                  type="button"
                  onClick={handleNextYear}
                  title="下一年"
                  className="p-1 rounded-md hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-double-right-line text-sm"></i>
                </button>
              </div>
            </div>

            {/* 星期 Header */}
            <div className="grid grid-cols-7 text-center mb-1">
              {weekHeaders.map((w, index) => (
                <span
                  key={w}
                  className={`text-xs font-medium py-1 ${
                    index === 0 || index === 6 ? 'text-amber-500/80' : 'text-foreground-400'
                  }`}
                >
                  {w}
                </span>
              ))}
            </div>

            {/* 日期网格 7x6 */}
            <div className="grid grid-cols-7 gap-1 text-center">
              {calendarDays.map((item) => (
                <button
                  key={item.dateStr}
                  type="button"
                  disabled={item.isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectDate(item.dateStr);
                  }}
                  className={`h-8 w-8 mx-auto flex items-center justify-center rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    item.isDisabled
                      ? 'text-foreground-200 cursor-not-allowed hover:bg-transparent'
                      : item.isSelected
                      ? 'bg-primary-600 text-white shadow-md shadow-primary-500/30 font-semibold'
                      : item.isToday
                      ? 'bg-primary-50 text-primary-600 border border-primary-200 font-semibold'
                      : item.isCurrentMonth
                      ? 'text-foreground-800 hover:bg-background-100'
                      : 'text-foreground-300 hover:bg-background-50'
                  }`}
                >
                  {item.dayNumber}
                </button>
              ))}
            </div>

            {/* 快捷选项区 */}
            <div className="mt-3 pt-2.5 border-t border-background-100 flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={(e) => handleQuickSelect('today', e)}
                  className="px-2 py-1 rounded bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  今天
                </button>
                <button
                  type="button"
                  onClick={(e) => handleQuickSelect('week', e)}
                  className="px-2 py-1 rounded bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  一周后
                </button>
                <button
                  type="button"
                  onClick={(e) => handleQuickSelect('month', e)}
                  className="px-2 py-1 rounded bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  1个月后
                </button>
              </div>
              {internalValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-foreground-400 hover:text-red-500 transition-colors"
                >
                  清空
                </button>
              )}
            </div>
          </div>
        )}
      >
        {/* 触发框 */}
        <div
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          className={`w-full flex items-center justify-between bg-white border border-background-300/60 transition-all duration-200 cursor-pointer ${
            sizeStyles[size]
          } ${
            isOpen
              ? 'border-primary-400/80 ring-2 ring-primary-400/20 shadow-sm'
              : 'hover:border-background-400/80'
          } ${error ? 'border-red-400 focus:border-red-400 ring-2 ring-red-400/20' : ''} ${
            disabled ? 'bg-gray-50 text-foreground-400 cursor-not-allowed border-gray-200' : 'text-foreground-950'
          } ${inputClassName}`}
        >
          <span className="truncate flex-1 pr-2">
            {internalValue ? (
              <span className="text-foreground-950 font-normal">{internalValue}</span>
            ) : (
              <span className="text-foreground-400">{placeholder}</span>
            )}
          </span>

          <span className="flex items-center gap-1.5 shrink-0 text-foreground-400">
            {clearable && internalValue && !disabled && (
              <span
                onClick={handleClear}
                className="p-0.5 hover:text-foreground-700 hover:bg-foreground-100 rounded-full transition-colors"
                title="清空"
              >
                <i className="ri-close-line text-sm"></i>
              </span>
            )}
            <i className="ri-calendar-line text-base text-foreground-400"></i>
          </span>
        </div>
      </Tippy>

      {/* 错误提示 */}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default DatePicker;
